import { createPollSchema, type Channel, type CreatePollInput } from '@nexo/types';
import { Button, Field, IconButton, Input, Modal, Select, Switch, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Plus, X } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { errorMessage, errorsFromApi, validate } from '../../lib/forms';

const MAX_OPTIONS = 10;
const durations: { value: string; label: string }[] = [
  { value: 'none', label: 'Sin límite' },
  { value: '1', label: '1 hora' },
  { value: '8', label: '8 horas' },
  { value: '24', label: '1 día' },
  { value: '72', label: '3 días' },
  { value: '168', label: '1 semana' },
];

/** Question, 2–10 options, single or multiple choice and an optional closing time. */
export function CreatePollDialog({
  channel,
  parentId,
  open,
  onClose,
}: {
  channel: Channel;
  parentId?: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [duration, setDuration] = useState('24');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const reset = () => {
    setQuestion('');
    setOptions(['', '']);
    setMultiple(false);
    setDuration('24');
    setErrors({});
  };

  const create = useMutation({
    mutationFn: (input: CreatePollInput) => api.messages.createPoll(channel.id, input),
    onSuccess: (message) => {
      cache.upsertMessage(queryClient, message);
      reset();
      onClose();
    },
    onError: (err) => {
      const fieldErrors = errorsFromApi(err);
      if (fieldErrors) setErrors(fieldErrors);
      else toast({ title: t('No se pudo crear'), description: errorMessage(err), tone: 'error' });
    },
  });

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    // React events bubble through portals: the composer's form must not see this submit.
    event.stopPropagation();
    const filled = options.map((o) => o.trim()).filter(Boolean);
    const { data, errors: invalid } = validate(createPollSchema, {
      question,
      options: filled,
      multiple,
      durationHours: duration === 'none' ? null : Number(duration),
      ...(parentId ? { parentId } : {}),
    });
    if (invalid) return setErrors(invalid);
    create.mutate(data);
  }

  return (
    <Modal open={open} onClose={onClose} title={t('Nueva encuesta')}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label={t('Pregunta')} error={errors.question}>
          {(props) => (
            <Input
              {...props}
              autoFocus
              maxLength={300}
              placeholder={t('p. ej. ¿Qué día hacemos la retro?')}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          )}
        </Field>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-sm font-medium">{t('Opciones')}</legend>
          {options.map((option, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                aria-label={t('Opción {{n}}', { n: i + 1 })}
                placeholder={t('Opción {{n}}', { n: i + 1 })}
                maxLength={100}
                value={option}
                onChange={(e) =>
                  setOptions((list) => list.map((o, j) => (j === i ? e.target.value : o)))
                }
              />
              {options.length > 2 && (
                <IconButton
                  label={t('Quitar opción')}
                  size="sm"
                  onClick={() => setOptions((list) => list.filter((_, j) => j !== i))}
                >
                  <X />
                </IconButton>
              )}
            </div>
          ))}
          {errors.options && <p className="text-xs text-danger">{errors.options}</p>}
          {options.length < MAX_OPTIONS && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={<Plus className="size-4" />}
              className="self-start"
              onClick={() => setOptions((list) => [...list, ''])}
            >
              {t('Añadir opción')}
            </Button>
          )}
        </fieldset>
        <Switch
          icon={<ListChecks />}
          label={t('Permitir varias respuestas')}
          checked={multiple}
          onChange={setMultiple}
        />
        <Field label={t('Duración')}>
          {(props) => (
            <Select
              {...props}
              value={duration}
              onChange={setDuration}
              options={durations.map((d) => ({ value: d.value, label: t(d.label) }))}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <Button type="submit" loading={create.isPending}>
            {t('Publicar encuesta')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
